import { useEffect, useMemo, useState } from 'react';

export type Breakpoints = {
    [key: string]: number;
};

export type Breakpoint =
    | 'XXXS'
    | 'XXS'
    | 'XS'
    | 'MD'
    | 'LG'
    | 'XL'
    | 'XXL'
    | 'XXXL'
    | 'XXXXL';

export interface BreakpointKeys {
    XXXS: Breakpoint;
    XXS: Breakpoint;
    XS: Breakpoint;
    MD: Breakpoint;
    LG: Breakpoint;
    XL: Breakpoint;
    XXL: Breakpoint;
    XXXL: Breakpoint;
    XXXXL: Breakpoint;
}

export const BREAKPOINT_KEYS: BreakpointKeys = {
    XXXS: 'XXXS',
    XXS: 'XXS',
    XS: 'XS',
    MD: 'MD',
    LG: 'LG',
    XL: 'XL',
    XXL: 'XXL',
    XXXL: 'XXXL',
    XXXXL: 'XXXXL',
};

// These are the list of supported WIDTH breakpoints as per our UX design documentation.
export const WIDTH_BREAKPOINTS: Breakpoints = {
    [BREAKPOINT_KEYS.XXXS]: 320,
    [BREAKPOINT_KEYS.XXS]: 412,
    [BREAKPOINT_KEYS.XS]: 576,
    [BREAKPOINT_KEYS.MD]: 768,
    [BREAKPOINT_KEYS.LG]: 1024, // HD
    [BREAKPOINT_KEYS.XL]: 1366, // Full HD
    [BREAKPOINT_KEYS.XXL]: 1440, // QHD
    [BREAKPOINT_KEYS.XXXL]: 2560, // 2K QHD
    [BREAKPOINT_KEYS.XXXXL]: 3840, // 4k UHD
};

export const HEIGHT_BREAKPOINTS: Breakpoints = {
    [BREAKPOINT_KEYS.XXS]: 360,
    [BREAKPOINT_KEYS.XS]: 720,
    [BREAKPOINT_KEYS.MD]: 960,
    [BREAKPOINT_KEYS.LG]: 1120,
    [BREAKPOINT_KEYS.XL]: 1280,
    [BREAKPOINT_KEYS.XXL]: 1440,
    [BREAKPOINT_KEYS.XXXL]: 2160,
};

/**
 * useMediaQuery is a reusable helper hook that allows us to test whether the application has hit
 * specific media breakpoints. This allows the developer to apply custom logic or styles based
 * each desired breakpoint.
 * @param query A breakpoint string used by document to detect whenever it matches or stops matching.
 */
export function useMediaQuery(query: string): boolean {
    const mediaQuery = useMemo(() => window.matchMedia(query), [query]);
    const [match, setMatch] = useState(mediaQuery.matches);

    useEffect(() => {
        const onChange = (): void => setMatch(mediaQuery.matches);
        mediaQuery.addEventListener('change', onChange);

        return () => mediaQuery.removeEventListener('change', onChange);
    }, [mediaQuery]);

    return match;
}
